/* ==========================================================================
   Collection facets — filtering without a full page reload.

   Every change serialises the form, pushes the query string into history and
   re-renders the section from Liquid. Filter counts, availability and the
   product grid therefore always come from Shopify, never from local state.
   ========================================================================== */
(function () {
  'use strict';

  const Theme = window.MassrutineTheme || {};

  class CollectionFacets extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-facets-form]');
      this.panel = this.querySelector('[data-facets-panel]');
      this.toggle = this.querySelector('[data-facets-toggle]');
      if (!this.form) return;

      this.sectionId = this.dataset.sectionId;
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.debounce = null;

      this.form.addEventListener('input', this.onInput.bind(this));
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
      });

      this.addEventListener('click', this.onClick.bind(this));

      if (this.toggle) this.toggle.addEventListener('click', this.openPanel.bind(this));

      const close = this.querySelector('[data-facets-close]');
      if (close) close.addEventListener('click', this.closePanel.bind(this));

      this.onKeydown = function (event) {
        if (event.key === 'Escape') this.closePanel();
      }.bind(this);
      document.addEventListener('keydown', this.onKeydown);

      this.onPopState = function () {
        this.render(window.location.search, false);
      }.bind(this);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('popstate', this.onPopState);
    }

    /* ---------------------------------------------------------------------
       Panel
       --------------------------------------------------------------------- */
    openPanel() {
      if (!this.panel || this.desktop.matches) return;

      this.panel.hidden = false;
      void this.panel.offsetWidth;
      this.panel.classList.add('is-open');
      if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');

      Theme.lockScroll();
      this.releaseFocus = Theme.trapFocus(this.panel);
    }

    closePanel() {
      if (!this.panel || this.panel.hidden || this.desktop.matches) return;

      this.panel.classList.remove('is-open');
      if (this.toggle) {
        this.toggle.setAttribute('aria-expanded', 'false');
        this.toggle.focus({ preventScroll: true });
      }

      if (this.releaseFocus) {
        this.releaseFocus();
        this.releaseFocus = null;
      }

      Theme.unlockScroll();

      const panel = this.panel;
      setTimeout(function () {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      }, 300);
    }

    /* ---------------------------------------------------------------------
       Input handling
       --------------------------------------------------------------------- */
    onInput(event) {
      if (!event.target.matches('[data-facets-input]')) return;

      /* Price inputs get a longer debounce so typing does not fire per digit */
      const delay = event.target.type === 'number' ? 600 : 120;

      clearTimeout(this.debounce);
      this.debounce = setTimeout(this.apply.bind(this), delay);
    }

    onClick(event) {
      const link = event.target.closest('[data-facets-link], [data-facets-clear]');
      if (!link) return;

      event.preventDefault();

      const url = new URL(link.href, window.location.origin);
      this.render(url.search, true);
    }

    apply() {
      const params = new URLSearchParams();
      const data = new FormData(this.form);

      data.forEach(function (value, key) {
        if (value === '' || value == null) return;
        params.append(key, value);
      });

      this.render(params.toString() ? '?' + params.toString() : '', true);
    }

    /* ---------------------------------------------------------------------
       Render
       --------------------------------------------------------------------- */
    render(search, pushState) {
      const results = document.querySelector('[data-collection-results]');
      if (results) results.classList.add('is-loading');

      const path = window.location.pathname;
      const url = path + search;

      if (pushState) window.history.pushState({ search: search }, '', url);

      Theme.fetchSection(this.sectionId, url)
        .then(
          function (doc) {
            [
              '[data-collection-results]',
              '[data-facets]',
            ].forEach(function (selector) {
              const fresh = doc.querySelector(selector);
              const current = document.querySelector(selector);
              if (fresh && current) current.innerHTML = fresh.innerHTML;
            });

            document.dispatchEvent(new CustomEvent('theme:content-updated', { detail: { target: document } }));

            /* Re-running connectedCallback rebinds the freshly rendered inputs */
            this.connectedCallback();

            const heading = document.querySelector('[data-collection-results]');
            if (heading) {
              heading.classList.remove('is-loading');
              heading.setAttribute('tabindex', '-1');
              heading.focus({ preventScroll: true });
            }
          }.bind(this)
        )
        .catch(function () {
          /* Filtering is progressive enhancement: fall back to a real
             navigation so the visitor still gets their filtered results. */
          window.location.assign(url);
        });
    }
  }

  if (!customElements.get('collection-facets')) {
    customElements.define('collection-facets', CollectionFacets);
  }
})();
